import type { ComponentChildren } from "preact";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { cn } from "./cn";

export type AlertTone = "info" | "success" | "warning" | "danger";

const TONE: Record<AlertTone, string> = {
  info: "border-info-border bg-info-bg text-info-fg",
  success: "border-success-border bg-success-bg text-success-fg",
  warning: "border-warning-border bg-warning-bg text-warning-fg",
  danger: "border-danger-border bg-danger-bg text-danger-fg",
};

const ICON: Record<AlertTone, typeof CheckCircleIcon> = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: ExclamationCircleIcon,
};

export function Alert({
  tone = "info",
  title,
  children,
  action,
  onClose,
  icon,
  class: extraClass,
}: {
  tone?: AlertTone;
  title?: ComponentChildren;
  children?: ComponentChildren;
  action?: ComponentChildren;
  onClose?: () => void;
  icon?: ComponentChildren;
  class?: string;
}) {
  const ToneIcon = ICON[tone];
  return (
    <div
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      class={cn(
        "flex flex-wrap items-start gap-3 rounded-lg border px-4 py-3 text-sm",
        TONE[tone],
        extraClass,
      )}
    >
      <span class="flex-none pt-0.5">
        {icon ?? <ToneIcon class="h-5 w-5" aria-hidden />}
      </span>
      <div class="min-w-0 flex-1 space-y-1">
        {title && (
          <div class="font-medium text-fg">{title}</div>
        )}
        {children && <div class="text-sm">{children}</div>}
      </div>
      {(action || onClose) && (
        <div class="flex flex-none items-center gap-2 self-start">
          {action}
          {onClose && (
            <button
              type="button"
              aria-label="閉じる"
              onClick={onClose}
              // The ::after overlay widens the hit area to 40px (-inset-1.5 on
              // a 28px box) without changing the visual size or layout.
              class="relative inline-flex h-7 w-7 items-center justify-center rounded-md text-current/70 transition after:absolute after:-inset-1.5 hover:bg-bg/40 hover:text-current"
            >
              <XMarkIcon class="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
